import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";

type Variant = "simple" | "epigraph" | "verse";

const VARIANT_OPTIONS: { value: Variant; label: string; description: string }[] = [
  { value: "simple", label: "Simple quote", description: "Plain indented block quotation" },
  { value: "epigraph", label: "Epigraph", description: "Quote with author/source attribution (epigraph package)" },
  { value: "verse", label: "Verse", description: "Poetry — preserves line breaks" },
];

export function BlockquoteNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const text = (node.attrs.text as string) || "";
  const variant = ((node.attrs.variant as string) || "simple") as Variant;
  const attribution = (node.attrs.attribution as string) || "";

  const [isOpen, setIsOpen] = useState(false);
  const [draftText, setDraftText] = useState(text);
  const [draftVariant, setDraftVariant] = useState<Variant>(variant);
  const [draftAttribution, setDraftAttribution] = useState(attribution);

  const handleOpen = useCallback(() => {
    setDraftText(text);
    setDraftVariant(variant);
    setDraftAttribution(attribution);
    setIsOpen(true);
  }, [text, variant, attribution]);

  const handleSave = useCallback(() => {
    updateAttributes({
      text: draftText,
      variant: draftVariant,
      attribution: draftAttribution,
    });
    setIsOpen(false);
  }, [draftText, draftVariant, draftAttribution, updateAttributes]);

  // Visual treatment varies by variant — same border-left, but verse
  // keeps `white-space: pre-wrap` for line breaks, and epigraph appends
  // an `— attribution` em-dashed line below the body.
  const verse = variant === "verse";
  return (
    <NodeViewWrapper data-type="blockquote-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      {text ? (
        <blockquote
          className={styles.blockquote}
          onClick={handleOpen}
          style={{
            cursor: "pointer",
            borderLeft: "4px solid var(--lilia-border, #ddd)",
            paddingLeft: 16,
            margin: "1em 0",
            color: "var(--lilia-text-muted, #666)",
            fontStyle: "italic",
            whiteSpace: verse ? "pre-wrap" : "normal",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }} />
          {variant === "epigraph" && attribution && (
            <div
              style={{
                marginTop: 6,
                fontSize: "0.9em",
                color: "var(--lilia-text-muted, #888)",
                textAlign: "right",
                fontStyle: "normal",
              }}
            >
              — {attribution}
            </div>
          )}
        </blockquote>
      ) : (
        <blockquote
          className={styles.blockquote}
          onClick={handleOpen}
          style={{
            cursor: "pointer",
            borderLeft: "4px solid var(--lilia-border, #ddd)",
            paddingLeft: 16,
            margin: "1em 0",
            color: "var(--lilia-text-muted, #666)",
            fontStyle: "italic",
          }}
        >
          <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add quote...</span>
        </blockquote>
      )}

      <BlockEditModal
        sourceNode={node}
        title="Edit Quote"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={600}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Style</label>
            <select
              value={draftVariant}
              onChange={(e) => setDraftVariant(e.target.value as Variant)}
              style={{
                width: "100%", padding: "8px 10px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                background: "var(--lilia-surface, #fff)",
              }}
            >
              {VARIANT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p style={{ marginTop: 4, fontSize: 11, color: "var(--lilia-text-muted, #888)" }}>
              {VARIANT_OPTIONS.find((o) => o.value === draftVariant)?.description}
            </p>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              {draftVariant === "verse" ? "Verse (one line per line break)" : "Quote text"}
            </label>
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder={draftVariant === "verse" ? "One line per verse line…" : "Enter quote text…"}
              rows={6}
              autoFocus
              style={{
                width: "100%", padding: "12px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                resize: "vertical", fontStyle: "italic", lineHeight: 1.6,
              }}
            />
          </div>
          {draftVariant === "epigraph" && (
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Attribution / source</label>
              <input
                value={draftAttribution}
                onChange={(e) => setDraftAttribution(e.target.value)}
                placeholder="e.g. Albert Einstein, Out of My Later Years (1950)"
                style={{
                  width: "100%", padding: "8px 10px", fontSize: 13,
                  border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                }}
              />
            </div>
          )}
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
